import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Mail, HeartHandshake, Ticket, ArrowRight } from 'lucide-react'
import { capability, CONSULTING } from '@gjl/voice/pricing'

// This page is a slim hub: the systems it used to describe now have dedicated
// solution pages. It stays alive so existing links and search results resolve.

const email = capability('email')!
const donorMgmt = capability('donor-management')!
const ticketing = capability('ticketing')!
const intensive = CONSULTING.find((c) => c.id === 'strategy-intensive')!

const SYSTEMS = [
  {
    icon: Mail,
    title: 'Email Marketing',
    href: '/services/email-marketing',
    price: `$${email.priceMonthly}/mo`,
    description: 'Newsletters, sequences, and season campaigns. Self-serve today, or fully managed.',
  },
  {
    icon: HeartHandshake,
    title: 'Donor Management',
    href: '/services/donor-management',
    price: `$${donorMgmt.priceMonthly}/mo`,
    description: 'Accepting donations is free (0% platform fee). This is the layer that grows giving.',
  },
  {
    icon: Ticket,
    title: 'Ticketing',
    href: '/services/ticketing',
    price: `$${ticketing.priceMonthly}/mo + ${ticketing.perUnitFee}`,
    description: 'Sell on your own site with QR check-in and reporting, not on a marketplace.',
  },
]

export default function DigitalSystemsPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-systems', breadcrumbLabels)

  useSEO({
    title: 'Digital Systems for Music Organizations',
    description:
      'Email marketing, donor management, and ticketing on one platform with one audience record. Each system has its own page and price.',
    url: '/services/digital-systems',
    canonical: '/services/digital-systems',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Digital Systems for Music Organizations',
      description:
        'Email marketing, donor management, and ticketing systems for music organizations, unified on one platform.',
      url: '/services/digital-systems',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const systemsRef = useStaggeredGrid<HTMLDivElement>('.system-card', { stagger: 0.1 })

  return (
    <div className="mx-auto max-w-6xl space-y-20 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">The systems behind the season</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Email, giving, and ticketing each have their own page now, because each is its own decision. What they share
          is the part that matters: one platform, one audience record.
        </p>
      </section>

      {/* System cards */}
      <section ref={systemsRef} className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
        {SYSTEMS.map((sys) => (
          <motion.div
            key={sys.href}
            className="system-card border-border flex flex-col rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <sys.icon className="text-primary mb-4 h-8 w-8" />
            <h2 className="text-foreground text-xl font-semibold">{sys.title}</h2>
            <div className="text-primary mt-1 text-sm font-bold">{sys.price}</div>
            <p className="text-muted-foreground mt-3 flex-1 text-sm">{sys.description}</p>
            <Link to={sys.href} className="mt-6 block">
              <Button variant="outline" className="w-full cursor-pointer">
                Learn More <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        ))}
      </section>

      {/* Unified record + consulting */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">Why one platform beats three tools</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          Your subscribers, donors, and ticket buyers are the same people. When the systems share one audience record,
          every campaign can see giving history, every appeal can see attendance, and nothing drifts between
          spreadsheets.
        </p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          Not sure what your organization needs first? The {intensive.name} ({intensive.price}) is an audit plus roadmap
          plus setup, and the plan is yours either way. Details under{' '}
          <Link to="/packages#consulting" className="text-primary underline">
            consulting
          </Link>
          .
        </p>
        <div className="pt-4">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
